import JSZip from 'jszip';

/**
 * Parse a CSV line respecting quoted fields that may contain commas
 * @param {string} line - A single CSV line
 * @returns {string[]} - Array of field values
 */
const parseCSVLine = (line) => {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      fields.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current.trim());
  return fields;
};

/**
 * Normalize known CSV column headers to their expected display format.
 * Handles backend headers that use special characters (e.g. subscript CO₂)
 * and maps them to the client-expected plain-text equivalents.
 *
 * @param {string} header - Raw header from CSV
 * @returns {string} - Normalized header
 */
const normalizeHeader = (header) => {
  const headerMap = {
    "Est. CO\u2082 (mt)": "Est. CO\u00B2 (mt)",
    "CO\u2082 Point": "CO\u00B2 Point",
  };
  return headerMap[header] || header;
};

/**
 * Parse CSV file for print preview
 * @param {string} csvContent - Raw CSV content as string
 * @returns {Object} - Parsed data with metadata and table data
 */
export const parseCSVForPrint = (csvContent) => {
  const lines = csvContent.split('\n').map(line => line.trim()).filter(line => line);

  const metadata = {};
  const tableData = {
    headers: [],
    rows: []
  };

  let metadataEndIndex = 0;

  // Parse metadata section: lines with exactly 2 fields where first field is a label
  // Metadata ends when we encounter a line with more than 2 fields,
  // or a line that doesn't follow key-value pattern
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const parts = parseCSVLine(line);

    // Metadata lines have exactly 2 parts (key, value)
    if (parts.length === 2) {
      const key = parts[0].trim();
      const value = parts[1].trim();
      if (key && value) {
        metadata[key] = value;
      }
      metadataEndIndex = i + 1;
    } else {
      // First line that doesn't match key-value pattern is the table header
      break;
    }
  }

  // After metadata, skip single-field lines (section titles like "Best Of 5 Vehicle")
  // and find the first line with multiple fields as the actual table header
  let headerIndex = metadataEndIndex;
  for (let i = metadataEndIndex; i < lines.length; i++) {
    const parts = parseCSVLine(lines[i]);
    if (parts.length > 1) {
      headerIndex = i;
      break;
    }
  }

  // Parse table section starting from headerIndex
  if (headerIndex < lines.length) {
    const headerLine = lines[headerIndex];
    tableData.headers = parseCSVLine(headerLine).map(normalizeHeader);

    for (let i = headerIndex + 1; i < lines.length; i++) {
      const rowLine = lines[i];
      if (rowLine) {
        const rowData = parseCSVLine(rowLine);
        // Accept rows that match header count, or pad/trim to match
        if (rowData.length > 0) {
          // Pad with empty strings if row has fewer columns
          while (rowData.length < tableData.headers.length) {
            rowData.push('');
          }
          // Trim if row has more columns than headers
          tableData.rows.push(rowData.slice(0, tableData.headers.length));
        }
      }
    }
  }

  return {
    metadata,
    tableData
  };
};

/**
 * Parse CSV file with multi-section format (e.g., Vehicle Performance reports).
 * Handles CSV files that have metadata section followed by multiple named table sections.
 *
 * @param {string} csvContent - Raw CSV content as string
 * @returns {Object} - Parsed data with metadata and sections array
 */
export const parseCSVMultiSection = (csvContent) => {
  const allLines = csvContent.split('\n').map(line => line.trim());

  const metadata = {};
  const sections = [];
  let metadataEndIndex = 0;

  // Filter non-empty lines for metadata parsing
  const nonEmptyLines = allLines.filter(line => line);

  // Parse metadata section: lines with exactly 2 fields
  for (let i = 0; i < nonEmptyLines.length; i++) {
    const parts = parseCSVLine(nonEmptyLines[i]);
    if (parts.length === 2) {
      const key = parts[0].trim();
      const value = parts[1].trim();
      if (key && value) {
        metadata[key] = value;
      }
      metadataEndIndex = i + 1;
    } else {
      break;
    }
  }

  // Parse sections after metadata.
  // A section = optional title line, then a header line, then data rows.
  //
  // Title line  : only one non-empty field, and that field is non-numeric text
  //               (e.g. "Best Of 5 Vehicle" or "Best Of 5 Vehicle,,,,,,").
  // Header line : more than one non-empty field, all of them non-numeric text
  //               (e.g. "Rank,Total Point,Plate Number,...").
  //
  // A title is only accepted when the following line is header-like, so a sparse
  // data row (single non-empty text cell) is never mistaken for a section title.
  // Reports without any title get one implicit section with an empty title.
  const parsedLines = [];
  for (let i = metadataEndIndex; i < nonEmptyLines.length; i++) {
    parsedLines.push(parseCSVLine(nonEmptyLines[i]));
  }

  const nonEmptyFields = (parts) => parts.filter(p => p.trim());

  const isTextValue = (value) => value !== '' && isNaN(value);

  const isHeaderLike = (parts) => {
    const filled = nonEmptyFields(parts);
    return filled.length > 1 && filled.every(p => isTextValue(p.trim()));
  };

  const isTitleLike = (parts, nextParts) => {
    const filled = nonEmptyFields(parts);
    if (filled.length !== 1) return false;
    if (!isTextValue(filled[0].trim())) return false;
    return Boolean(nextParts) && isHeaderLike(nextParts);
  };

  let currentSection = null;

  const startSection = (title) => {
    if (currentSection) {
      sections.push(currentSection);
    }
    currentSection = { title, headers: [], rows: [] };
  };

  for (let i = 0; i < parsedLines.length; i++) {
    const parts = parsedLines[i];

    if (isTitleLike(parts, parsedLines[i + 1])) {
      startSection(nonEmptyFields(parts)[0].trim());
      continue;
    }

    if (parts.length < 2) {
      continue;
    }

    if (!currentSection) {
      // Header line without a preceding title: single unnamed section
      startSection('');
    }

    if (currentSection.headers.length === 0) {
      currentSection.headers = parts.map(normalizeHeader);
      continue;
    }

    const rowData = [...parts];
    while (rowData.length < currentSection.headers.length) {
      rowData.push('');
    }
    currentSection.rows.push(rowData.slice(0, currentSection.headers.length));
  }

  // Push the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return {
    metadata,
    sections
  };
};

const getCSVFileFromZip = async (zipData) => {
  const zipContent = await JSZip.loadAsync(zipData);
  const filename = Object.keys(zipContent.files).find(candidate =>
    !zipContent.files[candidate].dir && candidate.toLowerCase().endsWith('.csv')
  );

  if (!filename) {
    throw new Error('No CSV file found in ZIP archive');
  }

  return {
    file: zipContent.files[filename],
    filename: filename.split(/[\\/]/).pop()
  };
};

/**
 * Extract CSV content from ZIP file
 * @param {ArrayBuffer} zipData - ZIP file data as ArrayBuffer
 * @returns {Promise<string>} - Extracted CSV content
 */
const extractCSVFromZip = async (zipData) => {
  try {
    const { file } = await getCSVFileFromZip(zipData);
    return file.async('text');
  } catch (error) {
    console.error('Error extracting CSV from ZIP:', error);
    throw new Error('Failed to extract CSV from ZIP file');
  }
};

const hasZipSignature = (fileData) => {
  const bytes = new Uint8Array(fileData);
  if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    return false;
  }

  return (
    (bytes[2] === 0x03 && bytes[3] === 0x04) ||
    (bytes[2] === 0x05 && bytes[3] === 0x06) ||
    (bytes[2] === 0x07 && bytes[3] === 0x08)
  );
};

/**
 * Prepare report bytes for a browser CSV download.
 * ZIP responses are unwrapped so archive bytes are never saved with a .csv name.
 *
 * @param {ArrayBuffer} fileData - Downloaded response bytes
 * @param {string} fallbackFilename - Filename to use for a plain CSV response
 * @param {string} contentType - Response Content-Type header
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const prepareCSVDownload = async (
  fileData,
  fallbackFilename,
  contentType = ''
) => {
  const normalizedContentType = contentType.toLowerCase();
  const isZip = (
    normalizedContentType.includes('application/zip') ||
    normalizedContentType.includes('application/x-zip-compressed') ||
    fallbackFilename.toLowerCase().endsWith('.zip') ||
    hasZipSignature(fileData)
  );

  if (!isZip) {
    return {
      blob: new Blob([fileData], { type: contentType || 'text/csv;charset=utf-8' }),
      filename: fallbackFilename
    };
  }

  try {
    const { file, filename } = await getCSVFileFromZip(fileData);
    const csvBytes = await file.async('uint8array');

    return {
      blob: new Blob([csvBytes], { type: 'text/csv;charset=utf-8' }),
      filename
    };
  } catch (error) {
    console.error('Error preparing CSV download:', error);
    throw new Error('Failed to extract CSV from ZIP file');
  }
};

const XLSX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * A raw .xlsx is itself a ZIP whose root contains the Open XML package parts,
 * most reliably identified by "[Content_Types].xml". The report backend, in
 * contrast, delivers report.xlsx wrapped inside an outer ZIP archive, so the
 * downloaded bytes carry a ZIP signature either way. This checks for the Open
 * XML marker to tell an already-usable workbook apart from the outer archive.
 *
 * @param {JSZip} zipContent - Loaded ZIP contents
 * @returns {boolean} - True when the ZIP is itself an xlsx package
 */
const isXlsxPackage = (zipContent) =>
  Object.keys(zipContent.files).some(
    (candidate) => candidate.toLowerCase() === '[content_types].xml'
  );

/**
 * Locate the .xlsx entry inside the outer report ZIP archive.
 *
 * @param {ArrayBuffer} zipData - Outer ZIP archive bytes
 * @returns {Promise<{file: JSZip.JSZipObject, filename: string}>}
 */
const getXLSXFileFromZip = async (zipData) => {
  const zipContent = await JSZip.loadAsync(zipData);
  const filename = Object.keys(zipContent.files).find(
    (candidate) =>
      !zipContent.files[candidate].dir &&
      candidate.toLowerCase().endsWith('.xlsx')
  );

  if (!filename) {
    throw new Error('No XLSX file found in ZIP archive');
  }

  return {
    file: zipContent.files[filename],
    filename: filename.split(/[\\/]/).pop()
  };
};

/**
 * Prepare report bytes for a browser XLSX download.
 *
 * The report backend wraps report.xlsx inside an outer ZIP archive. Saving
 * those archive bytes verbatim with an .xlsx name produces a file Excel cannot
 * open ("We found a problem with some content..."), because the archive is the
 * container that holds the workbook, not the workbook itself. This unwraps the
 * outer ZIP and returns the extracted workbook. A response that is already a
 * raw .xlsx (Open XML package) or a non-ZIP payload is passed through verbatim.
 *
 * @param {ArrayBuffer} fileData - Downloaded response bytes
 * @param {string} fallbackFilename - Filename to use when not extracting from a ZIP
 * @param {string} contentType - Response Content-Type header
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const prepareXLSXDownload = async (
  fileData,
  fallbackFilename,
  contentType = ''
) => {
  // Not a ZIP container at all -> save the response verbatim.
  if (!hasZipSignature(fileData)) {
    return {
      blob: new Blob([fileData], { type: contentType || XLSX_CONTENT_TYPE }),
      filename: fallbackFilename
    };
  }

  try {
    const zipContent = await JSZip.loadAsync(fileData);

    // Already a raw workbook (Open XML package) -> save verbatim.
    if (isXlsxPackage(zipContent)) {
      return {
        blob: new Blob([fileData], { type: contentType || XLSX_CONTENT_TYPE }),
        filename: fallbackFilename
      };
    }

    // Outer report archive -> extract the .xlsx entry.
    const { file, filename } = await getXLSXFileFromZip(fileData);
    const xlsxBytes = await file.async('uint8array');

    return {
      blob: new Blob([xlsxBytes], { type: XLSX_CONTENT_TYPE }),
      filename
    };
  } catch (error) {
    console.error('Error preparing XLSX download:', error);
    throw new Error('Failed to extract XLSX from ZIP file');
  }
};

/**
 * Check if the response is a ZIP file based on content type or URL
 * @param {Response} response - Fetch response object
 * @param {string} url - Original URL
 * @returns {boolean}
 */
const isZipFile = (response, url) => {
  const contentType = response.headers.get('content-type') || '';
  return (
    contentType.includes('application/zip') ||
    contentType.includes('application/x-zip-compressed') ||
    url.toLowerCase().endsWith('.zip')
  );
};

/**
 * Fetch and parse CSV from URL (handles both CSV and ZIP files)
 * @param {string} url - URL of the CSV or ZIP file
 * @returns {Promise<Object>} - Parsed CSV data
 */
export const fetchAndParseCSV = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    let csvContent;

    // Check if it's a ZIP file
    if (isZipFile(response, url)) {
      // It's a ZIP file - extract CSV from it
      const zipData = await response.arrayBuffer();
      csvContent = await extractCSVFromZip(zipData);
    } else {
      // It's a plain CSV file
      csvContent = await response.text();
    }

    const parsed = parseCSVForPrint(csvContent);
    const { sections } = parseCSVMultiSection(csvContent);

    // Expose section data so PDF/print consumers can render one table per section
    // (e.g. "Best Of 5 Vehicle" / "Worst of 5 Vehicle" in Vehicle Performance reports).
    return { ...parsed, sections };
  } catch (error) {
    console.error('Error fetching/parsing CSV:', error);
    throw error;
  }
};

/** Read the first worksheet of a report workbook for the print preview. */
export const fetchAndParseXLSX = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const fileData = await response.arrayBuffer();
  const zip = await JSZip.loadAsync(fileData);
  const workbookData = isXlsxPackage(zip)
    ? fileData
    : await (await getXLSXFileFromZip(fileData)).file.async('arraybuffer');
  const { readSheet } = await import('read-excel-file/universal');
  const sheetRows = await readSheet(workbookData);
  const rows = sheetRows.map((row) => {
    const values = row.map((value) => value == null ? '' : String(value));
    while (values.length && !values[values.length - 1]) values.pop();
    return values;
  }).filter((row) => row.length);

  const metadata = {};
  let headerIndex = 0;
  while (headerIndex < rows.length && rows[headerIndex].length === 2) {
    const [key, value] = rows[headerIndex];
    if (key && value) metadata[key] = value;
    headerIndex++;
  }
  while (headerIndex < rows.length && rows[headerIndex].length < 2) headerIndex++;

  const headers = (rows[headerIndex] || []).map(normalizeHeader);
  return {
    metadata,
    tableData: {
      headers,
      rows: rows.slice(headerIndex + 1).map((row) =>
        headers.map((_, index) => row[index] || '')
      ),
    },
  };
};
